import axios from 'axios';
import { getToken } from './DaoService';

const API_URL = import.meta.env.VITE_APP_API_URL;

/**
 * Récupère la configuration IA du backend :
 * provider, provider_label, default_model, models[]
 *
 * @returns {Promise<{provider: string, provider_label: string, default_model: string, models: Array<{value: string, label: string}>}>}
 */
export async function fetchAiConfig() {
    const response = await axios.get(`${API_URL}/ai/config`, { timeout: 10000 });
    return response.data;
}

/**
 * Envoie un message à l'IA et retourne la réponse.
 * L'historique de la conversation est géré côté backend (1 conversation par
 * utilisateur) : on n'envoie ici que le nouveau message, jamais tout l'historique.
 *
 * @param {string} content
 * @param {string} model
 * @param {AbortSignal} signal
 * @returns {Promise<string>}
 */
export async function sendChat(content, model, signal = null) {
    const response = await axios.post(
        `${API_URL}/ai/chat`,
        { content, model },
        {
            headers: {
                'Content-Type':  'application/json',
                'Authorization': `Bearer ${getToken()}`,
            },
            signal,
            timeout: 120000, // 2 min max
        }
    );

    return response.data.content;
}

/**
 * Récupère l'historique complet de la conversation IA de l'utilisateur connecté.
 *
 * @returns {Promise<Array<{role: string, content: string}>>}
 */
export async function fetchConversation() {
    const response = await axios.get(`${API_URL}/ai/conversation`, {
        headers: authHeaders(),
        timeout: 15000,
    });
    return response.data.messages;
}

/**
 * Vide la conversation IA de l'utilisateur connecté (messages + résumé).
 *
 * @returns {Promise<void>}
 */
export async function clearConversation() {
    await axios.delete(`${API_URL}/ai/conversation`, {
        headers: authHeaders(),
        timeout: 15000,
    });
}

const authHeaders = () => ({
    'Content-Type':  'application/json',
    'Authorization': `Bearer ${getToken()}`,
});

/**
 * Demande une reformulation du nom. Renvoie { pertinent, suggestion }.
 * suggestion = null quand l'intitulé est déjà pertinent.
 *
 * @param {'tache'|'affaire'} type
 * @param {string} nom
 * @param {AbortSignal} signal
 * @returns {Promise<{pertinent: boolean, suggestion: string|null}>}
 */
export async function suggestName(type, nom, signal = null) {
    const response = await axios.post(
        `${API_URL}/ai/suggest/name`,
        { type, nom },
        { headers: authHeaders(), signal, timeout: 30000 }
    );
    return response.data;
}

/**
 * Demande une proposition de description à partir du nom.
 *
 * @param {'tache'|'affaire'} type
 * @param {string} nom
 * @param {string|null} description  Description actuelle à améliorer (optionnel)
 * @param {AbortSignal} signal
 * @returns {Promise<string>}
 */
export async function suggestDescription(type, nom, description = null, signal = null) {
    const response = await axios.post(
        `${API_URL}/ai/suggest/description`,
        { type, nom, description },
        { headers: authHeaders(), signal, timeout: 45000 }
    );
    return response.data.description;
}

/**
 * Demande un résumé markdown d'une tâche ou d'un dossier.
 *
 * @param {'tache'|'affaire'} type
 * @param {string} uid
 * @param {AbortSignal} signal
 * @returns {Promise<string>}
 */
export async function summarize(type, uid, signal = null) {
    const response = await axios.post(
        `${API_URL}/ai/summarize`,
        { type, uid },
        { headers: authHeaders(), signal, timeout: 60000 }
    );
    return response.data.summary;
}
